/**
 * Order file categories: PO (`po`) vs receipt/invoice (`receipt_invoice`).
 * API shape: `order_files: { po_files, receipt_files }`.
 */

/**
 * File extension from row metadata (filename, S3 key, or URL path).
 *
 * @param {Object} [row]
 * @returns {string}
 */
export function orderFileExtension(row) {
  const candidates = [
    row?.filename,
    row?.file?.name,
    row?.s3_image_key,
    row?.s3_key,
    row?.image_url ? row.image_url.split('?')[0] : '',
  ];
  for (const src of candidates) {
    if (!src || typeof src !== 'string') continue;
    const match = /\.([a-z0-9]+)$/i.exec(src);
    if (match) return match[1].toLowerCase();
  }
  return '';
}

/**
 * Display kind for an order file row.
 *
 * @param {Object} [row]
 * @param {{ category?: 'po'|'receipt' }} [opts]
 * @returns {'pdf'|'csv'|'spreadsheet'|'image'|'document'}
 */
export function orderFileMediaKind(row, opts = {}) {
  const ext = orderFileExtension(row);
  if (ext === 'pdf' || isPdfMediaUrl(row?.image_url)) {
    return 'pdf';
  }
  if (ext === 'csv') {
    return 'csv';
  }
  if (ext === 'xlsx' || ext === 'xls') {
    return 'spreadsheet';
  }

  const mime =
    row?.file?.type || row?.mime_type || row?.content_type || '';
  if (typeof mime === 'string') {
    const lower = mime.toLowerCase();
    if (lower.includes('pdf')) return 'pdf';
    if (lower.includes('csv')) return 'csv';
    if (lower.includes('spreadsheet') || lower.includes('excel')) {
      return 'spreadsheet';
    }
    if (lower.startsWith('image/')) return 'image';
  }

  if (opts.category === 'receipt') {
    return 'image';
  }

  return 'document';
}

/**
 * True while upload is in progress (no URL yet).
 *
 * @param {Object} [row]
 * @returns {boolean}
 */
export function orderFileRowIsPendingUpload(row) {
  return Boolean(row?.file_upload_pending) && !row?.image_url;
}

/**
 * @param {Object} [row]
 * @returns {Object}
 */
function normalizeOrderFileRow(row) {
  if (!row || typeof row !== 'object') {
    return row;
  }
  if (row.image_url && row.file_upload_pending) {
    return { ...row, file_upload_pending: false };
  }
  return row;
}

/**
 * True when a URL points at a PDF (ignores presigned query string).
 *
 * @param {string} [url]
 * @returns {boolean}
 */
export function isPdfMediaUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const pathname = new URL(url).pathname;
    return /\.pdf$/i.test(pathname);
  } catch {
    const withoutQuery = url.split('?')[0];
    return /\.pdf$/i.test(withoutQuery);
  }
}

/**
 * Whether an order_files row should render as PDF (not as <img> preview).
 *
 * @param {Object} [row]
 * @param {{ category?: 'po'|'receipt' }} [opts]
 * @returns {boolean}
 */
export function orderFileRowIsPdf(row, opts = {}) {
  return orderFileMediaKind(row, opts) === 'pdf';
}

/**
 * @returns {{ po_files: Array, receipt_files: Array }}
 */
export function emptyOrderFiles() {
  return { po_files: [], receipt_files: [] };
}

/**
 * Read grouped order files from API order payload.
 *
 * @param {Object|null|undefined} order
 * @returns {{ po_files: Array, receipt_files: Array }}
 */
export function normalizeOrderFiles(order) {
  const grouped = order?.order_files;
  if (!grouped || typeof grouped !== 'object') {
    return emptyOrderFiles();
  }
  return {
    po_files: Array.isArray(grouped.po_files)
      ? grouped.po_files.map(normalizeOrderFileRow)
      : [],
    receipt_files: Array.isArray(grouped.receipt_files)
      ? grouped.receipt_files.map(normalizeOrderFileRow)
      : [],
  };
}

/**
 * Whether a row is a persisted server file (numeric id) or pending local upload.
 *
 * @param {Object} row
 * @returns {boolean}
 */
export function isPersistedOrderFileRow(row) {
  if (!row || row.id == null) return false;
  const id = row.id;
  if (typeof id === 'number') return id > 0;
  if (typeof id === 'string') {
    return !id.startsWith('temp-') && /^\d+$/.test(id);
  }
  return false;
}

/**
 * Count files for validation (at least one PO or receipt required).
 *
 * @param {Array} poState
 * @param {Array} receiptState
 * @returns {number}
 */
export function countOrderFiles(poState, receiptState) {
  const countRows = rows =>
    (rows || []).filter(
      row => row?.file || isPersistedOrderFileRow(row)
    ).length;
  return countRows(poState) + countRows(receiptState);
}

/**
 * Pending rows only (local `.file` present).
 *
 * @param {Array} rows
 * @returns {Array}
 */
export function pendingOrderFileRows(rows) {
  return (rows || []).filter(row => row?.file);
}

/**
 * @param {{ po_files?: Array, receipt_files?: Array }} orderFiles
 * @returns {Array}
 */
export function allOrderFilesList(orderFiles) {
  const g = orderFiles || emptyOrderFiles();
  return [...(g.po_files || []), ...(g.receipt_files || [])];
}

/**
 * Build multipart `order_files` JSON manifest for new uploads only.
 *
 * @param {{ poPending?: Array, receiptPending?: Array }} opts
 * @returns {string}
 */
export function buildOrderFilesManifestJson({ poPending = [], receiptPending = [] }) {
  const po_files = poPending.map(row => ({
    id: String(row.id),
    type: row.file?.type || 'application/pdf',
    ...(row.file?.name ? { filename: row.file.name } : {}),
  }));
  const receipt_files = receiptPending.map(row => ({
    id: String(row.id),
    type: row.file?.type || 'application/octet-stream',
    ...(row.file?.name ? { filename: row.file.name } : {}),
  }));
  return JSON.stringify({
    po_files,
    receipt_files,
  });
}

/**
 * Split deleted ids by category on order update.
 *
 * @param {Object|null} orderObj - Saved order with order_files
 * @param {Array} currentPo
 * @param {Array} currentReceipt
 * @returns {{ po_file_ids: number[], receipt_file_ids: number[] }}
 */
export function buildOrderFilesToDelete(orderObj, currentPo, currentReceipt) {
  const saved = normalizeOrderFiles(orderObj);
  const po_file_ids = saved.po_files
    .filter(
      f =>
        isPersistedOrderFileRow(f) &&
        !currentPo.some(r => String(r.id) === String(f.id))
    )
    .map(f => Number(f.id));
  const receipt_file_ids = saved.receipt_files
    .filter(
      f =>
        isPersistedOrderFileRow(f) &&
        !currentReceipt.some(r => String(r.id) === String(f.id))
    )
    .map(f => Number(f.id));
  return { po_file_ids, receipt_file_ids };
}

/**
 * @param {*} presigned
 * @returns {boolean}
 */
export function isPresignedUrlsDict(presigned) {
  return (
    presigned &&
    typeof presigned === 'object' &&
    !Array.isArray(presigned) &&
    ('po_files' in presigned || 'receipt_files' in presigned)
  );
}

/**
 * Initialize modal state from order.
 *
 * @param {Object|null} orderObj
 * @returns {{ poFiles: Array, receiptFiles: Array }}
 */
export function orderFileStateFromOrder(orderObj) {
  if (!orderObj) {
    return { poFiles: [], receiptFiles: [] };
  }
  const { po_files, receipt_files } = normalizeOrderFiles(orderObj);
  return { poFiles: [...po_files], receiptFiles: [...receipt_files] };
}
